import { useCallback, useEffect, useRef, useState, type PointerEvent as RPointerEvent } from "react";
import { errorMessage, uploadFile } from "../lib/api";
import { cn } from "../lib/cn";
import { Dialog } from "./ui/Dialog";
import { Button, IconButton } from "./ui/Button";
import { toast } from "./ui/Toast";
import { IconMinus, IconRefresh, IconAdd } from "./icons";

/*
 * Shared image editor for avatars, community icons and banners: drag to move,
 * scroll / pinch / slider to zoom in and out, arrow keys to nudge. Produces a
 * cropped image at a fixed output size, ready to upload.
 */

export type ImageKind = "avatar" | "banner" | "community_icon" | "community_banner";

export const IMAGE_PRESETS: Record<ImageKind, { aspect: number; width: number; shape: "circle" | "rounded" | "rect"; title: string }> = {
  avatar: { aspect: 1, width: 512, shape: "circle", title: "Edit profile photo" },
  community_icon: { aspect: 1, width: 512, shape: "rounded", title: "Edit icon" },
  banner: { aspect: 3, width: 1500, shape: "rect", title: "Edit banner" },
  community_banner: { aspect: 16 / 5, width: 1600, shape: "rect", title: "Edit banner" },
};

const MAX_ZOOM = 5;

interface View {
  /** Scale relative to the image's natural size. */
  scale: number;
  x: number;
  y: number;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Couldn't read that image"));
    img.src = src;
  });
}

export function ImageEditorDialog({
  open,
  onOpenChange,
  src,
  kind,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  /** Object URL of a picked file, or the URL of the current image to re-frame. */
  src: string | null;
  kind: ImageKind;
  onConfirm: (blob: Blob) => Promise<void> | void;
}) {
  const preset = IMAGE_PRESETS[kind];
  const stageRef = useRef<HTMLDivElement | null>(null);
  // The dialog portal mounts its content after opening, so track the element in state:
  // measuring must wait until it actually exists.
  const [stageEl, setStageEl] = useState<HTMLDivElement | null>(null);
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [frame, setFrame] = useState({ w: 0, h: 0 });
  const [view, setView] = useState<View>({ scale: 1, x: 0, y: 0 });
  const [busy, setBusy] = useState(false);
  const drag = useRef<{ id: number; sx: number; sy: number; vx: number; vy: number } | null>(null);
  const pinch = useRef(new Map<number, { x: number; y: number }>());
  const pinchStart = useRef<{ d: number; scale: number } | null>(null);

  // Frame size follows the dialog width.
  useEffect(() => {
    const el = stageEl;
    if (!open || !el) return;
    const measure = () => {
      const maxW = el.clientWidth - 48;
      if (maxW <= 0) return;
      const maxH = 360;
      let w = maxW;
      let h = w / preset.aspect;
      if (h > maxH) (h = maxH), (w = h * preset.aspect);
      setFrame({ w: Math.round(w), h: Math.round(h) });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [open, stageEl, preset.aspect]);

  useEffect(() => {
    if (!open || !src) return;
    let alive = true;
    setImg(null);
    loadImage(src)
      .then((i) => alive && setImg(i))
      .catch((e) => toast.error(errorMessage(e)));
    return () => {
      alive = false;
    };
  }, [open, src]);

  const coverScale = img && frame.w ? Math.max(frame.w / img.naturalWidth, frame.h / img.naturalHeight) : 1;
  const containScale = img && frame.w ? Math.min(frame.w / img.naturalWidth, frame.h / img.naturalHeight) : 1;
  const minScale = Math.min(containScale, coverScale);
  const maxScale = coverScale * MAX_ZOOM;

  /** Keep the image over the frame: covering it when larger, inside it when smaller. */
  const clamp = useCallback(
    (v: View): View => {
      if (!img) return v;
      const scale = Math.min(maxScale, Math.max(minScale, v.scale));
      const w = img.naturalWidth * scale;
      const h = img.naturalHeight * scale;
      const cx = (lo: number, hi: number, n: number) => Math.min(hi, Math.max(lo, n));
      const x = w >= frame.w ? cx(frame.w - w, 0, v.x) : cx(0, frame.w - w, v.x);
      const y = h >= frame.h ? cx(frame.h - h, 0, v.y) : cx(0, frame.h - h, v.y);
      return { scale, x, y };
    },
    [img, frame, minScale, maxScale],
  );

  const reset = useCallback(() => {
    if (!img) return;
    const s = coverScale;
    setView(clamp({ scale: s, x: (frame.w - img.naturalWidth * s) / 2, y: (frame.h - img.naturalHeight * s) / 2 }));
  }, [img, coverScale, frame, clamp]);

  useEffect(() => reset(), [img, frame.w, frame.h]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Zoom keeping the point (px, py) in frame coordinates fixed. */
  const zoomTo = useCallback(
    (scale: number, px = frame.w / 2, py = frame.h / 2) => {
      setView((v) => {
        const s = Math.min(maxScale, Math.max(minScale, scale));
        const k = s / v.scale;
        return clamp({ scale: s, x: px - (px - v.x) * k, y: py - (py - v.y) * k });
      });
    },
    [frame, minScale, maxScale, clamp],
  );

  const framePoint = (e: { clientX: number; clientY: number }) => {
    const r = stageRef.current?.querySelector("[data-frame]")?.getBoundingClientRect();
    return r ? { x: e.clientX - r.left, y: e.clientY - r.top } : { x: frame.w / 2, y: frame.h / 2 };
  };

  const onPointerDown = (e: RPointerEvent) => {
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    pinch.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pinch.current.size === 2) {
      const [a, b] = [...pinch.current.values()];
      pinchStart.current = { d: Math.hypot(a.x - b.x, a.y - b.y), scale: view.scale };
      drag.current = null;
    } else drag.current = { id: e.pointerId, sx: e.clientX, sy: e.clientY, vx: view.x, vy: view.y };
  };
  const onPointerMove = (e: RPointerEvent) => {
    if (pinch.current.has(e.pointerId)) pinch.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pinchStart.current && pinch.current.size === 2) {
      const [a, b] = [...pinch.current.values()];
      const mid = framePoint({ clientX: (a.x + b.x) / 2, clientY: (a.y + b.y) / 2 });
      zoomTo(pinchStart.current.scale * (Math.hypot(a.x - b.x, a.y - b.y) / pinchStart.current.d), mid.x, mid.y);
      return;
    }
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    setView((v) => clamp({ ...v, x: d.vx + e.clientX - d.sx, y: d.vy + e.clientY - d.sy }));
  };
  const onPointerUp = (e: RPointerEvent) => {
    pinch.current.delete(e.pointerId);
    if (pinch.current.size < 2) pinchStart.current = null;
    if (drag.current?.id === e.pointerId) drag.current = null;
  };

  // Wheel zoom needs a non-passive listener to prevent page scroll.
  useEffect(() => {
    const el = stageEl;
    if (!el || !open) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const p = framePoint(e);
      setView((v) => {
        const s = Math.min(maxScale, Math.max(minScale, v.scale * Math.exp(-e.deltaY * 0.0015)));
        const k = s / v.scale;
        return clamp({ scale: s, x: p.x - (p.x - v.x) * k, y: p.y - (p.y - v.y) * k });
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  const onKeyDown = (e: React.KeyboardEvent) => {
    const step = e.shiftKey ? 20 : 4;
    const moves: Record<string, [number, number]> = { ArrowLeft: [step, 0], ArrowRight: [-step, 0], ArrowUp: [0, step], ArrowDown: [0, -step] };
    if (moves[e.key]) {
      e.preventDefault();
      const [dx, dy] = moves[e.key];
      setView((v) => clamp({ ...v, x: v.x + dx, y: v.y + dy }));
    } else if (e.key === "+" || e.key === "=") zoomTo(view.scale * 1.1);
    else if (e.key === "-") zoomTo(view.scale / 1.1);
    else if (e.key === "0") reset();
  };

  const save = async () => {
    if (!img || !frame.w) return;
    setBusy(true);
    try {
      const outW = preset.width;
      const outH = Math.round(outW / preset.aspect);
      const canvas = document.createElement("canvas");
      canvas.width = outW;
      canvas.height = outH;
      const ctx = canvas.getContext("2d")!;
      const k = outW / frame.w;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, view.x * k, view.y * k, img.naturalWidth * view.scale * k, img.naturalHeight * view.scale * k);
      const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, "image/webp", 0.92));
      if (!blob) throw new Error("Couldn't process the image");
      await onConfirm(blob);
      onOpenChange(false);
    } catch (e) {
      toast.error(errorMessage(e, "Couldn't save the image"));
    } finally {
      setBusy(false);
    }
  };

  // Slider works on a log scale so zooming feels even.
  const toSlider = (s: number) => (Math.log(s / minScale) / Math.log(maxScale / minScale)) * 100 || 0;
  const fromSlider = (v: number) => minScale * Math.pow(maxScale / minScale, v / 100);
  const zoomPct = Math.round((view.scale / coverScale) * 100);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={preset.title}
      description="Drag to move. Scroll, pinch or use the slider to zoom."
      size={preset.aspect > 1.5 ? "xl" : "lg"}
      footer={
        <>
          <Button variant="ghost" icon={IconRefresh} className="mr-auto" onClick={reset} disabled={!img}>
            Reset
          </Button>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy} disabled={!img} onClick={() => void save()}>
            Apply
          </Button>
        </>
      }
    >
      <div
        ref={(el) => {
          stageRef.current = el;
          setStageEl(el);
        }}
        tabIndex={0}
        onKeyDown={onKeyDown}
        aria-label="Image position. Arrow keys move, plus and minus zoom, 0 resets."
        className="relative isolate flex touch-none select-none items-center justify-center overflow-hidden rounded-lg bg-black/80 py-6 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        style={{ minHeight: frame.h ? frame.h + 48 : 240 }}
      >
        <div
          data-frame
          className={cn("relative cursor-grab overflow-visible active:cursor-grabbing", preset.shape === "circle" ? "rounded-full" : preset.shape === "rounded" ? "rounded-[22%]" : "rounded-md")}
          style={{ width: frame.w, height: frame.h, boxShadow: "0 0 0 9999px rgb(0 0 0 / 0.6)" }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {img && (
            <img
              src={img.src}
              alt=""
              draggable={false}
              className="pointer-events-none absolute left-0 top-0 max-w-none origin-top-left"
              style={{ width: img.naturalWidth, height: img.naturalHeight, transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`, zIndex: -1 }}
            />
          )}
          {/* Rule-of-thirds guides */}
          <div aria-hidden className={cn("pointer-events-none absolute inset-0 border border-white/50", preset.shape === "circle" ? "rounded-full" : preset.shape === "rounded" ? "rounded-[22%]" : "rounded-md")}>
            {preset.shape === "rect" && (
              <>
                <span className="absolute inset-y-0 left-1/3 border-l border-white/15" />
                <span className="absolute inset-y-0 left-2/3 border-l border-white/15" />
                <span className="absolute inset-x-0 top-1/3 border-t border-white/15" />
                <span className="absolute inset-x-0 top-2/3 border-t border-white/15" />
              </>
            )}
          </div>
        </div>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <IconButton size="sm" label="Zoom out" icon={IconMinus} onClick={() => zoomTo(view.scale / 1.2)} disabled={!img} />
        <input
          type="range"
          min={0}
          max={100}
          step={0.5}
          value={toSlider(view.scale)}
          onChange={(e) => zoomTo(fromSlider(Number(e.target.value)))}
          aria-label="Zoom"
          className="flex-1 accent-[var(--accent)]"
          disabled={!img}
        />
        <IconButton size="sm" label="Zoom in" icon={IconAdd} onClick={() => zoomTo(view.scale * 1.2)} disabled={!img} />
        <span className="w-12 text-right text-sm text-fg-3 tabular">{zoomPct}%</span>
      </div>
      {view.scale < coverScale - 1e-6 && <p className="mt-2 text-xs text-fg-3">Zoomed out past the edges: the uncovered area stays transparent.</p>}
    </Dialog>
  );
}

/**
 * Pick → edit → upload, in one hook. Render `dialog` somewhere, call `pick()` to choose
 * a new file or `adjust(url)` to re-frame an existing image.
 */
export function useImageEditor(kind: ImageKind, onUploaded: (media: { id: string; url: string }) => void) {
  const [src, setSrc] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const input = useRef<HTMLInputElement | null>(null);
  const objectUrl = useRef<string | null>(null);

  const release = () => {
    if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
    objectUrl.current = null;
  };
  useEffect(() => release, []);

  const pick = () => {
    if (!input.current) {
      const el = document.createElement("input");
      el.type = "file";
      el.accept = "image/png,image/jpeg,image/webp,image/avif,image/gif";
      el.onchange = () => {
        const f = el.files?.[0];
        el.value = "";
        if (!f) return;
        if (f.size > 15 * 1024 * 1024) return toast.error("Images can be up to 15 MB");
        release();
        objectUrl.current = URL.createObjectURL(f);
        setSrc(objectUrl.current);
        setOpen(true);
      };
      input.current = el;
    }
    input.current.click();
  };

  const adjust = (url: string) => {
    release();
    setSrc(url);
    setOpen(true);
  };

  const dialog = (
    <ImageEditorDialog
      open={open}
      onOpenChange={setOpen}
      src={src}
      kind={kind}
      onConfirm={async (blob) => {
        const file = new File([blob], `${kind}.webp`, { type: "image/webp" });
        const m = await uploadFile(file, kind);
        onUploaded({ id: m.id, url: m.url });
      }}
    />
  );
  return { pick, adjust, dialog };
}
